import { useMemo } from "react"

import { Avatar } from "@coval/ui/avatar"
import { Badge } from "@coval/ui/badge"
import { Button } from "@coval/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@coval/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { Table, type ColumnDef } from "@coval/ui/table"
import { useCopyToClipboardFeedback } from "@coval/ui/use-copy-to-clipboard-feedback"
import { Copy, Mail, MoreHorizontal, UserMinus } from "lucide-react"

import { type MemberSummary, type OrganizationRole } from "../types"

interface MemberTableProps {
  members: readonly MemberSummary[]
  currentUserId: string
  canManage: boolean
  busy: boolean
  lastAdminId?: string
  onRoleChange: (member: MemberSummary, role: OrganizationRole) => void
  onRemove: (member: MemberSummary) => void
}

function MemberActions({
  member,
  canManage,
  removalDisabled,
  onRemove,
}: {
  member: MemberSummary
  canManage: boolean
  removalDisabled: boolean
  onRemove: (member: MemberSummary) => void
}) {
  const { copied, copy } = useCopyToClipboardFeedback()
  const label = `Actions for ${member.name || member.email}`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          icon
          aria-label={label}
          tooltip={copied ? "Copied!" : label}
          tooltipProps={{ forceOpen: copied }}
        >
          <MoreHorizontal size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={!member.email}
          onSelect={() => {
            void copy(member.email)
          }}
        >
          <Mail size={16} />
          Copy Email
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            void copy(member.id)
          }}
        >
          <Copy size={16} />
          Copy User ID
        </DropdownMenuItem>
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            destructive
            disabled={!canManage || removalDisabled}
            onSelect={() => onRemove(member)}
          >
            <UserMinus size={16} />
            Remove
          </DropdownMenuItem>
        </>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function buildMemberColumns({
  currentUserId,
  canManage,
  busy,
  lastAdminId,
  onRoleChange,
  onRemove,
}: Omit<MemberTableProps, "members">): ColumnDef<MemberSummary>[] {
  return [
    {
      id: "user",
      header: "User",
      width: 360,
      minWidth: 220,
      cell: (member) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar
            name={member.name || member.email}
            src={member.image_url}
            decorative
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-medium text-coval-text">
              <span className="truncate">{member.name || member.email}</span>
              {member.id === currentUserId && (
                <Badge variant="outline">You</Badge>
              )}
            </div>
            <div className="truncate text-coval-text-muted">{member.email}</div>
          </div>
        </div>
      ),
    },
    {
      id: "joined",
      header: "Joined",
      width: 144,
      minWidth: 140,
      flex: false,
      cell: (member) => (
        <span className="whitespace-nowrap text-coval-text-muted">
          {new Date(member.joined_at).toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      id: "role",
      header: "Role",
      width: 144,
      minWidth: 144,
      flex: false,
      cell: (member) => (
        <Select
          value={member.role}
          onValueChange={(role) =>
            onRoleChange(member, role as OrganizationRole)
          }
          disabled={
            !canManage ||
            busy ||
            member.id === lastAdminId ||
            member.id === currentUserId
          }
        >
          <SelectTrigger
            aria-label={`Role for ${member.name}`}
            className="w-32"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {member.role !== "org:admin" && member.role !== "org:member" && (
              <SelectItem value={member.role} disabled>
                {member.role}
              </SelectItem>
            )}
            <SelectItem value="org:admin">Admin</SelectItem>
            <SelectItem value="org:member">Member</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      headerLabel: "Actions",
      width: 48,
      minWidth: 48,
      flex: false,
      align: "right",
      pinned: "right",
      enableResizing: false,
      enableReordering: false,
      enableHiding: false,
      cell: (member) => (
        <MemberActions
          member={member}
          canManage={canManage}
          removalDisabled={
            !canManage ||
            busy ||
            member.id === lastAdminId ||
            member.id === currentUserId
          }
          onRemove={onRemove}
        />
      ),
    },
  ]
}

export function MemberTable({
  members,
  currentUserId,
  canManage,
  busy,
  lastAdminId,
  onRoleChange,
  onRemove,
}: MemberTableProps) {
  const sortedMembers = useMemo(
    () =>
      [...members].sort((left, right) =>
        (left.name || left.email).localeCompare(
          right.name || right.email,
          undefined,
          { sensitivity: "base" },
        ),
      ),
    [members],
  )
  const columns = useMemo(
    () =>
      buildMemberColumns({
        currentUserId,
        canManage,
        busy,
        lastAdminId,
        onRoleChange,
        onRemove,
      }),
    [busy, canManage, currentUserId, lastAdminId, onRemove, onRoleChange],
  )

  return (
    <Table
      aria-label="Organization members"
      data={sortedMembers}
      columns={columns}
      getRowId={(member) => member.id}
      emptyState="No members found."
      estimateRowHeight={() => 64}
      framed
    />
  )
}
